import { useEffect, useState } from 'react';
import { generateWaveform } from '../utils/waveformGenerator';

interface UseWaveformGeneratorProps {
  songUrl?: string;
  isReady: boolean;
  isStream?: boolean;
  bars?: number;
}

/**
 * Generates waveform data for audio
 * - For streams: Returns fake waveform (efficient, no fetching)
 * - For MP3/full files: Does real Web Audio API analysis
 */
export const useWaveformGenerator = ({
  songUrl,
  isReady,
  isStream = false,
  bars = 60,
}: UseWaveformGeneratorProps) => {
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!songUrl || !isReady) {
      setWaveformData([]);
      return;
    }

    let cancelled = false;

    const loadWaveform = async () => {
      setIsLoading(true);
      try {
        const waveform = await generateWaveform(songUrl, bars, isStream);
        if (!cancelled) {
          setWaveformData(waveform);
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[Waveform] Error generating waveform:', error);
        }
        if (!cancelled) {
          setWaveformData([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadWaveform();

    return () => {
      cancelled = true;
    };
  }, [songUrl, isReady, isStream, bars]);

  return { waveformData, isLoading };
};
